import type {ComboboxItem} from '@mantine/core';
import type {
  NotificationRuleRequest,
  NotificationRuleResponse,
  NotificationRuleSimpleResponse,
  NotificationVariable,
} from 'src/api/ProactApiClient';
import {
  CourseAssignmentStateEnum,
  CourseSessionContentStateEnum,
  CourseSessionStateEnum,
  EqualityOperatorEnum,
  FormStateEnum,
  InspectionStateEnum,
  ItemAssignmentStateEnum,
  ModuleEnum,
  NotificationAttributeEnum,
  NotificationRuleChangeConditionPropertyEnum,
  NotificationRuleChangeTypeEnum,
  NotificationRuleCourseChangeTypeEnum,
  NotificationRuleReminderConditionPropertyEnum,
  NotificationRuleTypeEnum,
  RelationalOperatorEnum,
  RoleEnum,
  SafetyEventStateEnum,
  TaskAssignmentStateEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import type {IListItem} from 'src/types/Form';
import fieldTypeDictionary from 'src/shared/constants/fieldTypeDictionary';
import type {IFormField, ISelectOption} from 'src/types/Form';
import {mapEnumToComboboxData} from 'src/utilities/enumUtils';

export const MapNotificationRuleResponseToNotificationRuleRequest = (
  data: NotificationRuleResponse
): NotificationRuleRequest => {
  return {
    id: data.id,
    name: data.name,
    active: data.active,
    metadata: data.metadata,
    subject: data.subject,
    body: data.body,
    ruleType: data.ruleType,
    projectId: data.projectId,
  };
};

export const CreateDefaultNotificationRule = (): NotificationRuleResponse => {
  return {
    id: 0,
    name: 'My new notification',
    active: true,
    subject: 'Hello {{USER_NAME}}!',
    body: '{{SUBJECT}} is due on {{DUE_DATE}}. That is only {{DUE_DAYS}} days away. Please make sure to complete it on time! <br /> <br /> If you forgot where it is, try copying this link into your browser: <br /> <br /> {{URL}}',
    ruleType: NotificationRuleTypeEnum.Reminder,
    creatorId: 0,

    metadata: {
      toUserAttributes: [],
      copyUserAttributes: [],
      blindCopyUserAttributes: [],

      toUserGroupIds: [],
      copyUserGroupIds: [],
      blindCopyUserGroupIds: [],

      module: ModuleEnum.Actions,

      reminderConditions: [],

      changeType: NotificationRuleChangeTypeEnum.Invalid,
      formChangeConditions: [],
      formFieldInvariantIds: [],
      notificationVariableFormFieldInvariantIds: [],
      formFieldPersonnelTargets: [],

      courseSessionChangeConditions: [],
      courseContentChangeConditions: [],
      courseContentInvariantIds: [],

      itemAssignmentChangeConditions: [],

      taskAssignmentChangeConditions: [],

      safetyEventChangeConditions: [],

      personnelChangeConditions: [],
    },
  } as NotificationRuleResponse;
};

export const GetNotificationRulePillConfig = (
  data?: NotificationRuleResponse | NotificationRuleSimpleResponse
): PillProps[] => {
  const pills: PillProps[] = [];
  if (data?.active === true) {
    pills.push({text: 'Active', color: 'green'});
  } else if (data?.active === false) {
    pills.push({text: 'Inactive', color: 'yellow'});
  }

  switch (data?.ruleType) {
    case NotificationRuleTypeEnum.ChangeEvent:
      pills.push({text: 'Type: Change', color: 'blue'});

      break;

    case NotificationRuleTypeEnum.Reminder:
      pills.push({text: 'Type: Reminder', color: 'blue'});

      break;

    case NotificationRuleTypeEnum.Schedule:
      pills.push({text: 'Type: Schedule', color: 'blue'});

      break;

    default:
      break;
  }

  switch (data?.module) {
    case ModuleEnum.AssignedForms:
      pills.push({text: 'Module: Assigned Forms', color: 'blue'});
      break;

    case ModuleEnum.Actions:
      pills.push({text: 'Module: Actions', color: 'blue'});
      break;

    case ModuleEnum.ToolboxTalks:
      pills.push({text: 'Module: Toolbox Talks', color: 'blue'});
      break;

    case ModuleEnum.Forms:
      pills.push({text: 'Module: Forms', color: 'blue'});
      break;

    case ModuleEnum.Training:
      pills.push({text: 'Module: Training', color: 'blue'});
      break;

    case ModuleEnum.ItemTrackerAssignments:
      pills.push({text: 'Module: Item Tracker', color: 'blue'});
      break;

    case ModuleEnum.SafetyEvent:
      pills.push({text: 'Module: Safety Event', color: 'blue'});
      break;

    case ModuleEnum.Attachments:
      pills.push({text: 'Module: Attachments', color: 'blue'});
      break;

    case ModuleEnum.Personnel:
      pills.push({text: 'Module: Personnel', color: 'blue'});
      break;

    case ModuleEnum.Inspections:
      pills.push({text: 'Module: Inspections', color: 'blue'});
      break;

    default:
      break;
  }

  return pills;
};

export const NotificationRuleDocumentTitle = 'Notifications';

export const NotificationTypeOptions: ISelectOption[] = [
  {
    id: Number(NotificationRuleTypeEnum.Reminder),
    label: 'I want to send reminders.',
  },
  {
    id: Number(NotificationRuleTypeEnum.ChangeEvent),
    label: 'I want to notify people of changes in the system.',
  },
];

export const FormChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Form Status',
  },
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.InputValueChange),
    label: 'Field Value',
  },
];

export const CourseChangeTypeOptions: ISelectOption[] = [
  {
    id: Number(NotificationRuleCourseChangeTypeEnum.CourseAssignmentChange),
    label: 'I want to target course assignments.',
  },
  {
    id: Number(NotificationRuleCourseChangeTypeEnum.CourseSessionChange),
    label: 'I want to target individual course sessions.',
  },
  {
    id: Number(NotificationRuleCourseChangeTypeEnum.CourseContentChange),
    label: 'I want to target specific content inside of a course session.',
  },
];

export const CourseAssignmentChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Assignment Status',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.TrainingEnrollmentChange
    ),
    label: 'Enrollment Status',
  },
];

export const CourseSessionChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Session Status',
  },
];

export const CourseContentChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Content Status',
  },
];

export const ItemAssignmentChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Status',
  },
];

export const TaskAssignmentChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Status',
  },
];

export const SafetyEventChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Status',
  },
];

export const InspectionChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Status',
  },
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.AssigneeChanged),
    label: 'Assignee Changed',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.InspectionGenerated
    ),
    label: 'Inspection Generated',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.InspectionScheduleChanged
    ),
    label: 'Schedule Changed',
  },
];

export const AttachmentChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.AttachmentClassification
    ),
    label: 'Classification',
  },
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.AttachmentType),
    label: 'Attachment Type',
  },
];

export const PersonnelChangeConditionPropertyOptions: ComboboxItem[] = [
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.PersonnelOperationType
    ),
    label: 'Operation Type',
  },
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.StatusChange),
    label: 'Status',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.PersonnelUserType
    ),
    label: 'User Type',
  },
  {
    value: String(NotificationRuleChangeConditionPropertyEnum.PersonnelRole),
    label: 'Role',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.PersonnelSuspended
    ),
    label: 'Suspended',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.PersonnelLeaveOfAbsence
    ),
    label: 'Leave of Absence',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.PersonnelEligibility
    ),
    label: 'Eligibility',
  },
  {
    value: String(
      NotificationRuleChangeConditionPropertyEnum.PersonnelBannedOnProperty
    ),
    label: 'Banned on Property',
  },
];

// Personnel operation types (matches backend PersonnelChangeOperationEnum)
export const PersonnelChangeOperationOptions: ComboboxItem[] = [
  {value: '1', label: 'Created'},
  {value: '2', label: 'Updated'},
  {value: '3', label: 'Deleted'},
];

export const UserStatusOptions: ComboboxItem[] = [
  {value: String(UserStatusEnum.Active), label: 'Active'},
  {value: String(UserStatusEnum.Inactive), label: 'Inactive'},
  {value: String(UserStatusEnum.Pending), label: 'Pending'},
];

export const UserTypeOptions: ComboboxItem[] = [
  {value: String(UserTypeEnum.Employee), label: 'Employee'},
  {value: String(UserTypeEnum.Contractor), label: 'Contractor'},
];

export const RoleOptions: ComboboxItem[] = [
  {value: String(RoleEnum.InstanceAdmin), label: 'Instance Admin'},
  {value: String(RoleEnum.InstanceUser), label: 'Instance User'},
  {value: String(RoleEnum.InstanceObserver), label: 'Instance Observer'},
  {value: String(RoleEnum.InstanceSupervisor), label: 'Instance Supervisor'},
  {
    value: String(RoleEnum.SafetyRepresentative),
    label: 'Safety Representative',
  },
  {value: String(RoleEnum.NotificationOnly), label: 'Notification Only'},
];

export const BooleanYesNoOptions: ComboboxItem[] = [
  {value: 'true', label: 'Yes'},
  {value: 'false', label: 'No'},
];

export const GetReminderConditionPropertyOptions = (
  module: ModuleEnum
): ISelectOption[] => {
  switch (Number(module)) {
    case ModuleEnum.Actions:
    case ModuleEnum.Approvals:
    case ModuleEnum.AssignedForms:
    case ModuleEnum.ToolboxTalks:
      return [
        {
          id: NotificationRuleReminderConditionPropertyEnum.DueDate,
          label: 'Due Date',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.DueInDays,
          label: 'Due In Days',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.PastDueDays,
          label: 'Past Due Days',
        },
      ];

    case ModuleEnum.Training:
      return [
        {
          id: NotificationRuleReminderConditionPropertyEnum.DueDate,
          label: 'Due Date',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.DueInDays,
          label: 'Due In Days',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.PastDueDays,
          label: 'Past Due Days',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.ExpiresInDays,
          label: 'Expires in Days',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.UnlocksInDays,
          label: 'Unlocks In Days',
        },
      ];

    case ModuleEnum.Forms:
      return [
        {
          id: NotificationRuleReminderConditionPropertyEnum.DaysInStatus,
          label: 'Days In Status',
        },
      ];

    case ModuleEnum.Attachments:
      return [
        {
          id: NotificationRuleReminderConditionPropertyEnum.ExpiresInDays,
          label: 'Expires in Days',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.Classification,
          label: 'Classification',
        },
        {
          id: NotificationRuleReminderConditionPropertyEnum.AttachmentType,
          label: 'Attachment Type',
        },
      ];
    default:
      break;
  }

  return [];
};

export const EqualityOperatorOptions: ComboboxItem[] = [
  {value: String(EqualityOperatorEnum.EqualTo), label: 'is'},
  {value: String(EqualityOperatorEnum.NotEqualTo), label: 'is not'},
];

export const RelationalOperatorOptions: ISelectOption[] = [
  {id: RelationalOperatorEnum.EqualTo, label: 'is'},
  {id: RelationalOperatorEnum.NotEqualTo, label: 'is not'},
  {id: RelationalOperatorEnum.LessThan, label: 'less than'},
  {
    id: RelationalOperatorEnum.LessThanOrEqualTo,
    label: 'less than or equal to',
  },
  {id: RelationalOperatorEnum.GreaterThan, label: 'greater than'},
  {
    id: RelationalOperatorEnum.GreaterThanOrEqualTo,
    label: 'greater than or equal to',
  },
];

export const FormStateOptions: ComboboxItem[] = [
  {value: String(FormStateEnum.Draft), label: 'Draft'},
  {value: String(FormStateEnum.Published), label: 'Published'},
  {value: String(FormStateEnum.Failed), label: 'Failed'},
];

export const CourseSessionStateOptions: ComboboxItem[] = mapEnumToComboboxData(
  CourseSessionStateEnum
) as ComboboxItem[];

export const CourseAssignmentStateOptions: ComboboxItem[] =
  mapEnumToComboboxData(CourseAssignmentStateEnum) as ComboboxItem[];

export const CourseAssignmentEnrollmentOptions: ComboboxItem[] = [
  {value: 'true', label: 'Enrolled'},
  {value: 'false', label: 'Disenrolled'},
];

export const CourseContentStateOptions: ComboboxItem[] = mapEnumToComboboxData(
  CourseSessionContentStateEnum
) as ComboboxItem[];

export const TaskAssignmentStateOptions: ComboboxItem[] = mapEnumToComboboxData(
  TaskAssignmentStateEnum
) as ComboboxItem[];

export const ItemAssignmentStateOptions: ComboboxItem[] = mapEnumToComboboxData(
  ItemAssignmentStateEnum
) as ComboboxItem[];

export const SafetyEventStateOptions: ComboboxItem[] = mapEnumToComboboxData(
  SafetyEventStateEnum
) as ComboboxItem[];

export const InspectionStateOptions: ComboboxItem[] = mapEnumToComboboxData(
  InspectionStateEnum
) as ComboboxItem[];

// Boolean marker, no value picker.
export const BooleanMarkerOptions: ComboboxItem[] = [
  {value: 'true', label: 'Changed'},
];

export const TargetAttributeOptions: IListItem[] = [
  {
    value: String(NotificationAttributeEnum.Assignee),
    label: 'Assignee',
  } as IListItem,
  {
    value: String(NotificationAttributeEnum.Supervisor),
    label: 'Supervisor',
  } as IListItem,
  {
    value: String(NotificationAttributeEnum.SuperSupervisor),
    label: "Supervisor's Supervisor",
  } as IListItem,
];

export const notificationFields = {
  name: (disabled: boolean) =>
    ({
      name: 'name',
      label: 'Name',
      active: true,
      fullWidth: true,

      formFieldTypeGroup: fieldTypeDictionary['single-text'],
      type: 'single-text',
      required: true,
      htmlName: 'name',
      disabled: disabled,
    } as IFormField),
  active: (disabled: boolean) =>
    ({
      name: 'active',
      label: 'Active',
      type: 'yes-no',
      active: true,
      required: true,
      selectOptions: [],
      formFieldTypeGroup: {
        switch: true,
        options: {
          isNADisabled: true,
          isQuestionMarkAfterLabelDisabled: true,
        },
      },
      fullWidth: true,

      htmlName: 'active',
      metadata: {
        defaultValue: true,
        helpText:
          'Enable or disable the notification rule. If disabled, the rule will not trigger any notifications.',
      },
      disabled: disabled,
    } as IFormField),
  subject: (disabled: boolean) =>
    ({
      name: 'subject',
      label: '',
      active: true,
      fullWidth: true,

      formFieldTypeGroup: fieldTypeDictionary['single-text'],
      type: 'single-text',
      required: true,
      htmlName: 'subject',
      disabled: disabled,
    } as IFormField),
  body: (disabled: boolean) =>
    ({
      name: 'body',
      htmlName: 'body',
      label: '',
      active: true,
      fullWidth: true,

      formFieldTypeGroup: fieldTypeDictionary['rich-content'],
      type: 'rich-content',
      required: true,
      disabled: disabled,
    } as IFormField),
};

export const substituteNotificationVariablesWithSampleValue = (
  data: string,
  variables: NotificationVariable[]
): string => {
  let newData = data;

  // We use a regex match so that all occurences are found, not just the first one.
  Object.values(variables).forEach(variable => {
    const regex = new RegExp(`{{${variable.variable}}}`, 'g');
    newData = newData.replace(regex, variable.example ?? 'N/A');
  });
  return newData;
};

export type ReminderConditionValueInputKind =
  | 'text'
  | 'classification'
  | 'attachmentType';

export interface ReminderConditionPropertyConfig {
  operators: ISelectOption[];
  valueInputKind: ReminderConditionValueInputKind;
}

const EqualityRelationalOperatorOptions: ISelectOption[] =
  RelationalOperatorOptions.filter(
    opt =>
      opt.id === RelationalOperatorEnum.EqualTo ||
      opt.id === RelationalOperatorEnum.NotEqualTo
  );

export const getReminderConditionPropertyConfig = (
  property: NotificationRuleReminderConditionPropertyEnum
): ReminderConditionPropertyConfig => {
  switch (property) {
    case NotificationRuleReminderConditionPropertyEnum.Classification:
      return {
        operators: EqualityRelationalOperatorOptions,
        valueInputKind: 'classification',
      };

    case NotificationRuleReminderConditionPropertyEnum.AttachmentType:
      return {
        operators: EqualityRelationalOperatorOptions,
        valueInputKind: 'attachmentType',
      };

    default:
      return {
        operators: RelationalOperatorOptions,
        valueInputKind: 'text',
      };
  }
};

export const getReminderConditionPropertyDescription = (
  property: NotificationRuleReminderConditionPropertyEnum
): string => {
  switch (property) {
    case NotificationRuleReminderConditionPropertyEnum.DueDate:
      return "Days between today and the item's due date. Negative for past due, positive for upcoming.";

    case NotificationRuleReminderConditionPropertyEnum.DueInDays:
      return 'Days until the item is due. Excludes items due today or past due.';

    case NotificationRuleReminderConditionPropertyEnum.PastDueDays:
      return 'Days the item has been past its due date. "is 3" matches items due exactly 3 days ago.';

    case NotificationRuleReminderConditionPropertyEnum.DaysInStatus:
      return 'Days the form has been in its current status. "greater than 10" matches forms unchanged for over 10 days.';

    case NotificationRuleReminderConditionPropertyEnum.ExpiresInDays:
      return "Days until the item's expiration date. Negative values match items that have already expired.";

    case NotificationRuleReminderConditionPropertyEnum.UnlocksInDays:
      return 'Days until a locked training assignment becomes available to the user.';

    case NotificationRuleReminderConditionPropertyEnum.Classification:
      return 'Matches attachments tagged with the selected classification.';

    case NotificationRuleReminderConditionPropertyEnum.AttachmentType:
      return 'Matches attachments of the selected file or document type.';

    default:
      return '';
  }
};
